import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "socket.io-client";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    wrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
    },
    header: {
        padding: "8px 12px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        backgroundColor: "#fafafa",
        fontWeight: 600,
        color: "rgb(67, 83, 105)",
        fontSize: 14,
        flexShrink: 0,
    },
    list: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    empty: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: 14,
        padding: 24,
    },
    emptyTitle: {
        textAlign: "center",
        fontSize: 16,
        fontWeight: 600,
        margin: "8px 0 0",
        color: "rgb(67, 83, 105)",
    },
}));

const reducer = (state, action) => {
    if (action.type === "RESET") {
        return [];
    }

    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload || [];
        const merged = [...state];
        newTickets.forEach((ticket) => {
            const idx = merged.findIndex((t) => t.id === ticket.id);
            if (idx !== -1) {
                merged[idx] = ticket;
            } else {
                merged.push(ticket);
            }
        });
        return merged;
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;
        const idx = state.findIndex((t) => t.id === ticket.id);
        if (idx === -1) return state;
        const next = [...state];
        next[idx] = { ...next[idx], ...ticket };
        return next;
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;
        const idx = state.findIndex((t) => t.id === ticket.id);
        if (idx === -1) return state;
        const next = [...state];
        const [updated] = next.splice(idx, 1);
        next.unshift({ ...updated, ...ticket });
        return next;
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const idx = state.findIndex((t) => t.contactId === contact.id);
        if (idx === -1) return state;
        const next = [...state];
        next[idx] = { ...next[idx], contact };
        return next;
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        return state.filter((t) => t.id !== ticketId);
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;
        const idx = state.findIndex((t) => t.id === ticketId);
        if (idx === -1) return state;
        const next = [...state];
        next[idx] = { ...next[idx], unreadMessages: 0 };
        return next;
    }

    return state;
};

const AgendamentoTicketsList = ({
    dataAgendamento,
    searchParam,
    selectedQueueIds,
    selectedWhatsappIds,
    selectedTime,
    selectedTag,
    dataInicio,
    dataFim,
}) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [ticketsList, dispatch] = useReducer(reducer, []);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
        setHasMore(false);
    }, [dataAgendamento, searchParam, selectedQueueIds, selectedWhatsappIds, selectedTime, selectedTag, dataInicio, dataFim]);

    useEffect(() => {
        if (!dataAgendamento) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get("/tickets", {
                    params: {
                        searchParam,
                        pageNumber,
                        status: "open",
                        queueIds: JSON.stringify(selectedQueueIds),
                        whatsappIds: JSON.stringify(selectedWhatsappIds),
                        selectedTime,
                        selectedTag,
                        dataInicio,
                        dataFim,
                        dataAgendamento,
                    },
                });
                if (cancelled) return;
                dispatch({ type: "LOAD_TICKETS", payload: data.tickets });
                setHasMore(data.hasMore);
            } catch (err) {
                if (!cancelled) toastError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [dataAgendamento, pageNumber, searchParam, selectedQueueIds, selectedWhatsappIds, selectedTime, selectedTag, dataInicio, dataFim]);

    useEffect(() => {
        if (!dataAgendamento) return undefined;

        let urlsUnicas = new Set([process.env.REACT_APP_BACKEND_URL]);
        if (user && user.whatsapps) {
            user.whatsapps.map((w) => w.urlBackend).filter(Boolean).forEach((url) => urlsUnicas.add(url));
        }
        const sockets = [];
        [...urlsUnicas].forEach((url) => {
            const socket = openSocket(url.trim() + "/");
            sockets.push(socket);
            socket.on("connect", () => {
                socket.emit("joinTickets", "open");
            });

            socket.on("ticket", (data) => {
                if (data.action === "updateUnread") {
                    dispatch({ type: "RESET_UNREAD", payload: data.ticketId });
                }
                if (data.action === "update") {
                    dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
                }
                if (data.action === "delete") {
                    dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
                }
            });

            socket.on("appMessage", (data) => {
                if (data.action === "create") {
                    dispatch({ type: "UPDATE_TICKET_UNREAD_MESSAGES", payload: data.ticket });
                }
            });

            socket.on("contact", (data) => {
                if (data.action === "update") {
                    dispatch({ type: "UPDATE_TICKET_CONTACT", payload: data.contact });
                }
            });
        });

        return () => {
            sockets.forEach((s) => s.disconnect());
        };
    }, [dataAgendamento, user]);

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            setPageNumber((p) => p + 1);
        }
    };

    const formatHeaderDate = (iso) => {
        if (!iso) return "";
        const [y, m, d] = iso.split("-");
        if (!y || !m || !d) return iso;
        return `${d}/${m}/${y}`;
    };

    return (
        <Paper className={classes.wrapper} elevation={0}>
            <div className={classes.header}>
                {dataAgendamento
                    ? `Agendamentos de ${formatHeaderDate(dataAgendamento)}`
                    : "Selecione uma data no calendário"}
            </div>
            <Paper square elevation={0} className={classes.list} onScroll={handleScroll}>
                <List style={{ paddingTop: 0 }}>
                    {!dataAgendamento && (
                        <div className={classes.empty}>
                            <p className={classes.emptyTitle}>Nenhuma data selecionada</p>
                            <p>Clique em um dia do calendário para ver os agendamentos.</p>
                        </div>
                    )}
                    {dataAgendamento && loading && ticketsList.length === 0 && (
                        <TicketsListSkeleton />
                    )}
                    {dataAgendamento && !loading && ticketsList.length === 0 && (
                        <div className={classes.empty}>
                            <p className={classes.emptyTitle}>Nenhum agendamento</p>
                            <p>Não há tickets agendados para esta data.</p>
                        </div>
                    )}
                    {dataAgendamento &&
                        ticketsList.map((ticket) => (
                            <TicketListItem
                                ticket={ticket}
                                key={ticket.id}
                                multiplaSelecao={false}
                            />
                        ))}
                    {dataAgendamento && loading && ticketsList.length > 0 && (
                        <TicketsListSkeleton />
                    )}
                </List>
            </Paper>
        </Paper>
    );
};

export default AgendamentoTicketsList;
